import { Dialog } from '@cimplico/components';
import { useEffect, useState } from 'react';
import { toastNotification } from '../../../components/toast/Toast';
import { useDownloadLink } from '../../../queries/use-download-link';
import { FileRecordNew, FileStatusEnum } from '../../../types/file-record';
import { downloadDocument } from '../../../utilities/document-download';
import { useIsFirmRole } from '../../../hooks/use-is-firm-role';
import { useUserContext } from '../../../providers/UserProvider';
import { hasWriteAccess } from '../file-utils';

type DocumentDownloadConfirmProps = {
    fileRecords: FileRecordNew[];
    onClose: () => void;
};

function DocumentDownloadConfirm({ fileRecords, onClose }: DocumentDownloadConfirmProps): JSX.Element {
    const [enabled, setEnabled] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const { isExternalUser } = useIsFirmRole();
    const { assignedWorkpapers } = useUserContext();

    const downloadLinkQuery = useDownloadLink(
        fileRecords.map((r) => (r.source?.id ? r.source?.id : r.id)),
        enabled
    );

    const canCheckOut = hasWriteAccess(
        isExternalUser,
        assignedWorkpapers,
        fileRecords.map((f) => f.path)
    );
    const containCheckedOutFiles = fileRecords.find((f) => f.status === FileStatusEnum.CHECKED_OUT);

    let message;
    if (fileRecords.length === 1) {
        if (containCheckedOutFiles) {
            message = 'This document has already been checked out, and may not be the most recent version.';
        } else {
            message = `Do you want to download ${fileRecords[0].name}? ${canCheckOut ? 'This document will be checked out.' : ''}`;
        }
    } else {
        if (containCheckedOutFiles) {
            message = 'Some documents have already been checked out, and may not be the most recent version.';
        } else {
            message = `Do you want to download ${fileRecords.length} documents? ${
                canCheckOut ? 'These document will be checked out.' : ''
            }`;
        }
    }

    useEffect(() => {
        async function download(urls: { recordId: string; url: string }[]): Promise<void> {
            setDownloading(true);
            for (const { url, recordId } of urls) {
                const name = fileRecords.find((r) => r.id === recordId)?.name || 'filename.txt';
                await downloadDocument(name, url);
            }
            toastNotification('success', `${urls.length} ${urls.length === 1 ? 'document' : 'documents'} downloaded.`);
            setDownloading(false);
            onClose();
        }

        if (downloadLinkQuery.data?.urls && enabled) {
            download(downloadLinkQuery.data.urls);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [downloadLinkQuery.data?.urls, enabled]);

    return (
        <Dialog
            primaryAction="Download"
            secondaryAction="Cancel"
            title="Download Document"
            description={message}
            type="error"
            isOpen={true}
            onClose={onClose}
            onSecondaryActionClick={onClose}
            onPrimaryActionClick={(): void => {
                setEnabled(true);
            }}
            isLoading={downloadLinkQuery.isLoading || downloading}
        />
    );
}

export default DocumentDownloadConfirm;
